import React, { useState, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ReactSlider from 'react-slider'
import SearchPage from 'containers/SearchPage'
import { Select, Checkbox, Button, Table } from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'
import messages from './messages'

const AgeRangeSlider = ({ min, max, onChange }) => (
  <div className="mt-1">
    <div className="flex justify-between text-xs text-gray-500 mb-1">
      <span>{min}</span>
      <span>{max}</span>
    </div>
    <ReactSlider
      className="w-full h-5 flex items-center"
      thumbClassName="w-4 h-4 bg-primary rounded-full cursor-grab focus:outline-none focus:ring-2 focus:ring-primary"
      trackClassName="h-1 rounded"
      min={0}
      max={100}
      value={[min, max]}
      onChange={([newMin, newMax]) => onChange(newMin, newMax)}
      pearling
      minDistance={1}
      renderTrack={(props, state) => (
        <div
          {...props}
          className={`h-1 rounded ${state.index === 1 ? 'bg-primary' : 'bg-gray-200'}`}
        />
      )}
    />
  </div>
)

const DETAIL_URL = (id) => `/rest/v2/supplierpriority/${id}?includeRelations=1`

const populateFromDetail = (detail, allLanguages, allProducts) => ({
  minAge: detail.min_age ?? 0,
  maxAge: detail.max_age ?? 100,
  voiceStars: detail.include_all_voice_stars ?? false,
  languages: (detail.language_uids ?? [])
    .map((uid) => allLanguages.find((l) => l.value === uid))
    .filter(Boolean),
  products: (detail.product_uids ?? [])
    .map((uid) => allProducts.find((p) => p.value === uid))
    .filter(Boolean),
})

const PriorityForm = ({ priorityId, allLanguages, allProducts }) => {
  const { formatMessage } = useIntl()
  const [detail, loading, , refetch] = useApi(DETAIL_URL(priorityId))

  const [minAge, setMinAge] = useState(0)
  const [maxAge, setMaxAge] = useState(100)
  const [languages, setLanguages] = useState([])
  const [products, setProducts] = useState([])
  const [voiceStars, setVoiceStars] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveState, setSaveState] = useState(null)

  useEffect(() => {
    if (!detail) return
    const state = populateFromDetail(detail, allLanguages, allProducts)
    setMinAge(state.minAge)
    setMaxAge(state.maxAge)
    setVoiceStars(state.voiceStars)
    setLanguages(state.languages)
    setProducts(state.products)
  }, [detail, allLanguages, allProducts])

  const handleSave = async () => {
    setSaving(true)
    setSaveState(null)

    const res = await fetch(`/rest/v2/supplierpriority/${priorityId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        min_age: minAge,
        max_age: maxAge,
        language_uids: languages.map((l) => l.value),
        product_uids: products.map((p) => p.value),
        include_all_voice_stars: voiceStars,
      }),
    }).then((r) => r.json())

    setSaving(false)

    if (!res.success) {
      setSaveState('error')
      toastr.error((res.errors || []).join(', ') || 'Something went wrong')
      return
    }

    setSaveState('saved')
    refetch(DETAIL_URL(priorityId))
    toastr.success("Saved successfully")
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        <FormattedMessage {...messages.saving} />
      </div>
    )
  }

  if (!detail) return null

  return (
    <div className="p-6 space-y-6">
      <h3 className="text-base font-semibold text-gray-900">
        <FormattedMessage {...messages.editTitle} values={{ name: detail.name }} />
      </h3>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">
          <FormattedMessage {...messages.fieldAgeRange} />
          {' '}
          <span className="text-gray-500 font-normal">({minAge}–{maxAge})</span>
        </p>
        <AgeRangeSlider
          min={minAge}
          max={maxAge}
          onChange={(min, max) => { setMinAge(min); setMaxAge(max) }}
        />
      </div>

      <Select
        label={formatMessage(messages.fieldLanguages)}
        isMulti
        value={languages}
        options={allLanguages}
        onChange={setLanguages}
      />

      <Select
        label={formatMessage(messages.fieldProducts)}
        isMulti
        value={products}
        options={allProducts}
        onChange={setProducts}
      />

      <Checkbox
        label={formatMessage(messages.fieldVoiceStars)}
        checked={voiceStars}
        onChange={() => setVoiceStars((v) => !v)}
      />

      <div className="flex items-center gap-3 pt-2">
        <Button
          disabled={saving}
          onClick={handleSave}
        >
          {saving
            ? formatMessage(messages.saving)
            : saveState === 'saved'
              ? formatMessage(messages.saved)
              : formatMessage(messages.save)
          }
        </Button>
      </div>
    </div>
  )
}

const SupplierPrioritiesPage = (props) => {
  const { formatMessage } = useIntl()
  const [selectedPriority, setSelectedPriority] = useState(null)

  const [rawLanguages] = useApi('/rest/v2/language?pageSize=300')
  const [rawProducts] = useApi('/rest/v2/product?pageSize=200&search[hidden]=0')

  const allLanguages = useMemo(
    () => (rawLanguages || []).map((l) => ({ value: +l.uid, label: l.name })),
    [rawLanguages]
  )
  const allProducts = useMemo(
    () => (rawProducts || []).map((p) => ({ value: +p.uid, label: p.name })),
    [rawProducts]
  )

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <h2 className="text-lg font-semibold leading-6 text-gray-900">
          <FormattedMessage {...messages.title} />
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          <FormattedMessage {...messages.subtitle} />
        </p>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-1/2 border-r border-gray-200 overflow-auto">
          <SearchPage
            {...props}
            baseApiUrl="/rest/v2/supplierpriority"
            containerClassName="w-full"
            components={{
              Tr: ({ childProps = {}, ...innerProps }) => (
                <Table.Tr
                  {...innerProps}
                  onClick={childProps.uid ? () => setSelectedPriority(childProps) : undefined}
                  className={{
                    'cursor-pointer': !!childProps.uid,
                    'bg-blue-50': !!(childProps.uid && selectedPriority?.uid === childProps.uid),
                  }}
                />
              ),
            }}
            head={{
              name: { label: formatMessage(messages.columnName) },
              customer_name: { label: formatMessage(messages.columnCustomer) },
              age_range: { label: formatMessage(messages.columnAgeRange) },
              voice_stars: { label: formatMessage(messages.columnVoiceStars) },
            }}
            body={{
              name: (p) => (
                <span className="text-sm font-medium text-gray-900">{p.name}</span>
              ),
              customer_name: (p) => (
                <span className="text-sm text-gray-500">{p.customer_name || '—'}</span>
              ),
              age_range: (p) => (
                <span className="text-sm text-gray-500">{p.min_age}–{p.max_age}</span>
              ),
              voice_stars: (p) => (
                <span className={`text-sm ${p.voice_stars ? 'text-green-600' : 'text-gray-400'}`}>
                  {p.voice_stars ? '✓' : '—'}
                </span>
              ),
            }}
          />
        </div>

        <div className="w-1/2 overflow-auto">
          {selectedPriority ? (
            <PriorityForm
              key={selectedPriority.uid}
              priorityId={selectedPriority.uid}
              allLanguages={allLanguages}
              allProducts={allProducts}
            />
          ) : (
            <p className="text-sm text-gray-500 p-6">
              <FormattedMessage {...messages.noSelected} />
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default SupplierPrioritiesPage
